<template>
  <div class="components__simple-datepicker">
    <label>
      <div class="datepicker-label">
        <slot />
      </div>
      <input
        placeholder="Datum wählen"
        ref="target"
        v-bind:name="name"
        v-bind:value="modelValue"
      />
    </label>
  </div>
</template>

<style lang="scss" scoped>
.components__simple-datepicker {
  label {
    > div {
      font-size: 14px;
      margin-bottom: 5px;
    }
  }
}
</style>

<style lang="scss">
@import "flatpickr/dist/flatpickr.css";
.components__simple-datepicker {
  input[type="text"] {
    width: 100px;
    text-align: center;
    cursor: pointer;
  }
}
</style>

<script lang="ts" setup>
  import { computed, onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';

  import flatpickr from "flatpickr";
  import { BaseOptions } from 'flatpickr/dist/types/options';
  import { Instance } from 'flatpickr/dist/types/instance';
  import { German } from "flatpickr/dist/l10n/de.js";

  const props = defineProps({
    name: {
      // name attribute
      type: String,
      default: null,
    },
    modelValue: {
      // v-model
      type: [Date, String],
      default: () => new Date()
    },
    future: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: Date,
      default: null,
    },
    skipDefaultValue: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(["update:model-value"]);
  
  const { disabled } = toRefs(props);
  const picker = ref<Instance | null>(null);
  const target = ref<HTMLInputElement | null>(null);

  onMounted(() => {
    resetPicker();

    const handleDateChange = (selectedDates: Date[], dateStr: string, instance: Instance) => {
      emit("update:model-value", dateStr);
    };

    const options: Partial<BaseOptions> = {
      locale: German,
      altFormat: "d.m.Y",
      altInput: true,
      defaultDate: props.skipDefaultValue
        ? undefined
        : props.modelValue || props.minDate || new Date(),
      onValueUpdate: handleDateChange,
      onReady: handleDateChange,
      onChange: handleDateChange,
    };

    // Future dates allowed?
    if (!props.future) {
      options.maxDate = new Date();
    }

    if (props.minDate) {
      options.minDate = props.minDate;
    }

    if (target.value) {
      picker.value = flatpickr(target.value, options);
    }
  });

  onBeforeUnmount(() => {
    resetPicker();
  });
  
  const resetPicker = () => {
    if (picker.value) {
      picker.value.destroy();
      picker.value = null;
    }
  };
  const updatePickerDisabled = () => {
    if (!picker.value) return;

    if (props.disabled)
      picker.value._input.setAttribute("disabled", "disabled");
    else picker.value._input.removeAttribute("disabled");
  };

  watch([disabled, picker], () => {
    updatePickerDisabled();
  });
</script>
